import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listExerciseModes } from "@/api/catalog";
import { listFunctionalRoles } from "@/api/functionalRoles";
import { createAssignment } from "@/api/planning";
import { listPractitioners } from "@/api/practitioners";
import type { ExerciseMode, Practitioner, SlotTemplate } from "@/api/types";

type Props = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  year: number;
  month: number;
  date: string; // YYYY-MM-DD
  slot: SlotTemplate | null;
  /** Practitioner IDs already assigned to this (date, slot) — excluded from picker. */
  excludedPractitionerIds?: Set<string>;
  /** All active slots of the org, used to offer a start/end time range. */
  daySlots?: SlotTemplate[];
  /** Practitioners already busy, per slot id, on that date. */
  occupiedBySlot?: Map<string, Set<string>>;
};

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

function toMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * Slots reachable from `start` by walking forward while each slot's end_time
 * meets the next slot's start_time. The assignment model is one row per slot,
 * so a "3h" assignment is 3 contiguous rows — which the grid already renders
 * as a single block (see lib/dayBlocks.ts).
 */
function contiguousChain(start: SlotTemplate, all: SlotTemplate[]): SlotTemplate[] {
  const sorted = [...all].sort(
    (a, b) => toMinutes(a.start_time) - toMinutes(b.start_time),
  );
  const from = sorted.findIndex((s) => s.id === start.id);
  if (from < 0) return [start];
  const chain = [sorted[from]!];
  for (let i = from + 1; i < sorted.length; i++) {
    const prev = chain[chain.length - 1]!;
    const next = sorted[i]!;
    if (next.start_time !== prev.end_time) break;
    chain.push(next);
  }
  return chain;
}

export function AssignmentDialog({
  open,
  onClose,
  organizationId,
  year,
  month,
  date,
  slot,
  excludedPractitionerIds,
  daySlots,
  occupiedBySlot,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [exerciseMode, setExerciseMode] = useState<ExerciseMode | null>(null);
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  /** Slot the range starts on — defaults to the clicked cell. */
  const [functionalRoleId, setFunctionalRoleId] = useState("");
  const [startSlotId, setStartSlotId] = useState<string | null>(null);
  /** Number of contiguous slots covered — 1 = end at the start slot's end. */
  const [slotCount, setSlotCount] = useState(1);

  useEffect(() => {
    if (open) {
      setPractitioner(null);
      setExerciseMode(null);
      setComment("");
      setServerError(null);
      setFunctionalRoleId("");
      setStartSlotId(slot?.id ?? null);
      setSlotCount(1);
    }
  }, [open, slot]);

  const practitioners = useQuery({
    queryKey: ["practitioners-active-300"],
    queryFn: () => listPractitioners({ status: "ACTIVE", limit: 200 }),
    enabled: open,
  });

  const modes = useQuery({
    queryKey: ["exercise-modes-active"],
    queryFn: () => listExerciseModes(true),
    enabled: open,
  });

  // Functional roles scoped to this organization, so the picker shows the
  // phone line the holder will actually answer on.
  const roles = useQuery({
    queryKey: ["functional-roles-active", organizationId],
    queryFn: () =>
      listFunctionalRoles({ isActive: true, organizationId }),
    enabled: open,
  });

  // Default to PRES exercise mode once loaded
  useEffect(() => {
    if (modes.data && !exerciseMode) {
      const pres = modes.data.find((m) => m.code === "PRES");
      if (pres) setExerciseMode(pres);
    }
  }, [modes.data, exerciseMode]);

  // Every slot of the day, sorted — the candidate start times.
  const sortedSlots = useMemo(
    () =>
      [...(daySlots ?? (slot ? [slot] : []))].sort(
        (a, b) => toMinutes(a.start_time) - toMinutes(b.start_time),
      ),
    [daySlots, slot],
  );
  const startSlot = useMemo(
    () => sortedSlots.find((s) => s.id === startSlotId) ?? slot,
    [sortedSlots, startSlotId, slot],
  );
  // Contiguous slots reachable from the start — the candidate end times.
  const chain = useMemo(
    () => (startSlot ? contiguousChain(startSlot, sortedSlots) : []),
    [startSlot, sortedSlots],
  );
  const selectedSlots = useMemo(
    () => chain.slice(0, Math.max(1, Math.min(slotCount, chain.length))),
    [chain, slotCount],
  );
  const selectedMinutes = useMemo(
    () => selectedSlots.reduce((sum, s) => sum + s.duration_min, 0),
    [selectedSlots],
  );

  const availablePractitioners = useMemo(() => {
    if (!practitioners.data) return [];
    // Busy on the clicked cell, plus busy on any extra slot of the chosen range.
    // When the caller gives per-slot occupancy, it fully describes the chosen
    // range — the clicked cell may no longer even be part of it.
    const busy = occupiedBySlot
      ? new Set<string>()
      : new Set(excludedPractitionerIds ?? []);
    for (const s of selectedSlots) {
      for (const pid of occupiedBySlot?.get(s.id) ?? []) busy.add(pid);
    }
    if (busy.size === 0) return practitioners.data.items;
    return practitioners.data.items.filter((p) => !busy.has(p.id));
  }, [practitioners.data, excludedPractitionerIds, occupiedBySlot, selectedSlots]);

  // Drop the picked practitioner if extending the range makes them unavailable.
  useEffect(() => {
    if (!practitioner) return;
    if (!availablePractitioners.some((p) => p.id === practitioner.id)) {
      setPractitioner(null);
    }
  }, [availablePractitioners, practitioner]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!practitioner || !exerciseMode || selectedSlots.length === 0) {
        throw new Error("missing_fields");
      }
      // One row per slot — sequentially, so a mid-range conflict still leaves
      // the earlier hours assigned rather than rolling everything back.
      const failed: string[] = [];
      for (const s of selectedSlots) {
        try {
          await createAssignment(organizationId, year, month, {
            date,
            slot_template_id: s.id,
            practitioner_id: practitioner.id,
            exercise_mode_id: exerciseMode.id,
            functional_role_id: functionalRoleId || null,
            comment: comment || null,
          });
        } catch {
          failed.push(`${s.start_time.slice(0, 5)}–${s.end_time.slice(0, 5)}`);
        }
      }
      return failed;
    },
    onSuccess: (failed) => {
      qc.invalidateQueries({ queryKey: ["planning", organizationId, year, month] });
      if (failed.length > 0) {
        setServerError(t("planning.assignPartial", { slots: failed.join(", ") }));
        return;
      }
      onClose();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("planning.addAssignment")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {startSlot && (
            <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {date} · {startSlot.start_time.slice(0, 5)}–
                {(selectedSlots[selectedSlots.length - 1] ?? startSlot).end_time.slice(0, 5)}
                {" · "}
                {formatDuration(selectedMinutes)}
              </Typography>
            </Box>
          )}
          {startSlot && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>{t("planning.assignStartTime")}</InputLabel>
                <Select
                  label={t("planning.assignStartTime")}
                  value={startSlot.id}
                  onChange={(e) => {
                    setStartSlotId(String(e.target.value));
                    setSlotCount(1);
                  }}
                >
                  {sortedSlots.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.start_time.slice(0, 5)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t("planning.assignEndTime")}</InputLabel>
                <Select
                  label={t("planning.assignEndTime")}
                  value={Math.min(slotCount, chain.length) || 1}
                  onChange={(e) => setSlotCount(Number(e.target.value))}
                >
                  {chain.map((s, i) => {
                    const minutes = chain
                      .slice(0, i + 1)
                      .reduce((sum, x) => sum + x.duration_min, 0);
                    return (
                      <MenuItem key={s.id} value={i + 1}>
                        {s.end_time.slice(0, 5)}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {formatDuration(minutes)}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>
          )}
          {serverError && <Alert severity="error">{serverError}</Alert>}
          {(practitioners.isLoading || modes.isLoading) && <CircularProgress size={24} />}
          <Autocomplete
            options={availablePractitioners}
            getOptionLabel={(p) => p.display_name}
            value={practitioner}
            onChange={(_, v) => setPractitioner(v)}
            renderInput={(params) => (
              <TextField {...params} label={t("planning.selectPractitioner")} required />
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText={t("planning.noPractitionersAvailable")}
          />
          {(roles.data ?? []).length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t("planning.selectFunctionalRole")}</InputLabel>
              <Select
                label={t("planning.selectFunctionalRole")}
                value={functionalRoleId}
                onChange={(e) => setFunctionalRoleId(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>{t("planning.noFunctionalRole")}</em>
                </MenuItem>
                {(roles.data ?? []).map((r) => {
                  // Only this organization's post carries the line to display.
                  const post = r.posts[0];
                  return (
                    <MenuItem key={r.id} value={r.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                          {r.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.label}
                          {post?.phone_line ? ` · ${post.phone_line}` : ""}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel>{t("planning.selectExerciseMode")}</InputLabel>
            <Select
              label={t("planning.selectExerciseMode")}
              value={exerciseMode?.id ?? ""}
              onChange={(e) => {
                const m = modes.data?.find((x) => x.id === e.target.value) ?? null;
                setExerciseMode(m);
              }}
            >
              {(modes.data ?? []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {m.color && (
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: m.color }} />
                    )}
                    <Typography>{m.code}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.label}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Commentaire (optionnel)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={1}
            maxRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!practitioner || !exerciseMode || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
